import type { EnqueueLeadUseCase } from '@/application/use-cases/EnqueueLeadUseCase';
import type { DeviceSession } from '@/domain/entities/DeviceSession';
import { toWireSafeLeadCaptureInput } from '@/domain/entities/LeadCaptureInput';
import type { LeadCaptureInput } from '@/domain/entities/LeadCaptureInput';
import type { LeadData } from '@hub/shared-types';

export type CapturePluginLeadResult =
  | { success: true; showQueueAlert: boolean }
  | { success: false; reason: 'queue-full' }
  | { success: false; reason: 'storage-unavailable' };

function toLeadCaptureInput(lead: LeadData, consentAccepted: boolean): LeadCaptureInput {
  return {
    name: lead.name,
    email: lead.email ?? '',
    phone: lead.phone ?? null,
    // `LeadData` (contrato `@hub/plugin-sdk`/ADR-004) não tem campo `cpf` —
    // nenhum plugin desta fatia o captura.
    cpf: null,
    customFields: lead.customFields ?? {},
    consentAccepted,
  };
}

/**
 * Use case responsável por capturar um lead real, reportado por um plugin
 * ativo via `onLeadCapture` (contrato `PluginProps`, ADR-004) — HUB-237,
 * CA-15/CA-18/CA-20. Delega a `EnqueueLeadUseCase` (HUB-236, mesmo caminho
 * de `CaptureFakeLeadUseCase`): o lead é gravado na `lead_queue` do
 * IndexedDB e sincronizado em segundo plano, sem duplicar a lógica de
 * limiares/gravação entre os dois caminhos de captura.
 *
 * `consentAccepted` vem da decisão tomada em `PluginConsentGateScreen`
 * (CA-18), guardada pelo chamador — nunca do próprio `LeadData` (o plugin
 * não sabe nada sobre consentimento, por design). Recusa nunca bloqueia a
 * captura em si (o `onLeadCapture` do plugin sempre é atendido
 * normalmente); apenas os campos pessoais são sanitizados antes de
 * enfileirar (CA-20, `toWireSafeLeadCaptureInput`).
 */
export class CapturePluginLeadUseCase {
  constructor(private readonly enqueueLeadUseCase: EnqueueLeadUseCase) {}

  async execute(
    session: DeviceSession,
    lead: LeadData,
    consentAccepted: boolean,
    consentTextVersion: number
  ): Promise<CapturePluginLeadResult> {
    const input = toLeadCaptureInput(lead, consentAccepted);
    const wireInput = toWireSafeLeadCaptureInput(input);
    const result = await this.enqueueLeadUseCase.execute(session, wireInput, consentTextVersion);

    if (result.outcome === 'queue-full') {
      return { success: false, reason: 'queue-full' };
    }
    if (result.outcome === 'storage-unavailable') {
      return { success: false, reason: 'storage-unavailable' };
    }
    return { success: true, showQueueAlert: result.showQueueAlert };
  }
}
